<script lang="ts">
  // ============================================================
  // ✅ Svelte 5 Runes Component
  // ------------------------------------------------------------
  // 이 컴포넌트는 Svelte 5 runes 문법을 사용합니다.
  // ============================================================

  import FlashBanner from '@/components/FlashBanner.svelte';

  import { router } from '@inertiajs/svelte'
  import { formatRelativeTime } from '@/lib/utils'
  import CategoryBadge from '@/components/ui/CategoryBadge.svelte'
  import AdSlot from '@/components/ads/AdSlot.svelte'
  import { shouldShowAdAfter, IN_FEED_SLOT, IN_FEED_LAYOUT_KEY } from '@/lib/ads'

  
  interface Category {
    id: number;
    name: string;
    color: string;
  }
  
  interface Post {
    id: number;
    title: string;
    excerpt?: string;
    is_notice?: boolean;
    is_pinned?: boolean;
    categories?: Category[];
    user?: { name: string; avatar_url?: string };
    created_at: string;
    views_count?: number;
    comments_count?: number;
    likes_count?: number;
  }
  
  interface Pagination {
    current_page: number;
    total_pages: number;
  }

  interface Board {
    slug: string;
    name: string;
    description?: string;
    [key: string]: unknown;
  }

  type ListDensityMode = 'comfortable' | 'compact'

  let {
    board = {} as Board,
    posts = [] as Post[],
    popular_posts = [] as Post[],
    current_sort = 'hot',
    selected_category_id = null as number | string | null,
    pagination = {} as Pagination,
    categories = [] as Category[]
  } = $props()

  // 광고는 **일반 글만 세어** 7·14·21번째 뒤에 둔다 — 이 목록은 서버가 공지를
  // posts 에 섞어 보내므로 인덱스를 그대로 쓰면 앱(공지 별도 목록)과 기준이
  // 갈리고, 7번째가 공지면 광고가 통째로 빠진다.
  let adAfterPostIds = $derived.by(() => {
    const regular = posts.filter((p) => !p.is_notice)
    return new Set(regular.filter((_, i) => shouldShowAdAfter(i, regular.length)).map((p) => p.id))
  })

  let selectedCategory = $state<number | null>(null)
  let sortBy = $state('hot')
  const FAVORITE_BOARDS_KEY = 'favoriteBoards'
  const BOARD_LIST_DENSITY_KEY = 'boardListDensity'
  let isFavorite = $state(false)
  let listDensity = $state<ListDensityMode>('comfortable')

  function goToPage(page: number) {
    const params: Record<string, any> = { page, sort: sortBy }
    if (selectedCategory !== null) {
      params.category_id = selectedCategory
    }
    router.get(`/boards/${board.slug}`, params, { preserveState: true })
  }

  function changeSort(sort: string) {
    sortBy = sort
    const params: Record<string, any> = { sort }
    if (selectedCategory !== null) {
      params.category_id = selectedCategory
    }
    router.get(`/boards/${board.slug}`, params, { preserveState: true })
  }

  function changeCategory(categoryId: number | null) {
    selectedCategory = categoryId
    const params: Record<string, any> = { sort: sortBy, page: 1 }
    if (categoryId !== null) {
      params.category_id = categoryId
    }
    router.get(`/boards/${board.slug}`, params, { preserveState: true })
  }

  function getBoardDensityStorageKey() {
    return `${BOARD_LIST_DENSITY_KEY}:${board?.slug || 'default'}`
  }

  function loadListDensity() {
    if (typeof window === 'undefined') return

    const saved = localStorage.getItem(getBoardDensityStorageKey())
    if (saved === 'compact' || saved === 'comfortable') {
      listDensity = saved
    }
  }

  function changeListDensity(mode: ListDensityMode) {
    listDensity = mode
    if (typeof window !== 'undefined') {
      localStorage.setItem(getBoardDensityStorageKey(), mode)
    }
  }

  function checkFavorite() {
    if (typeof window === 'undefined' || !board?.slug) return

    try {
      const saved = localStorage.getItem(FAVORITE_BOARDS_KEY)
      const parsed = saved ? JSON.parse(saved) : {}
      isFavorite = Boolean(parsed?.[board.slug])
    } catch (error) {
      isFavorite = false
    }
  }

  function toggleBoardFavorite() {
    if (typeof window === 'undefined' || !board?.slug) return

    try {
      const saved = localStorage.getItem(FAVORITE_BOARDS_KEY)
      const parsed = saved ? JSON.parse(saved) : {}

      if (parsed?.[board.slug]) {
        delete parsed[board.slug]
        isFavorite = false
      } else {
        parsed[board.slug] = {
          slug: board.slug,
          name: board.name
        }
        isFavorite = true
      }

      localStorage.setItem(FAVORITE_BOARDS_KEY, JSON.stringify(parsed))
      window.dispatchEvent(new CustomEvent('favorite-boards:updated'))
    } catch (error) {
      isFavorite = false
    }
  }

  $effect(() => {
    checkFavorite()
    loadListDensity()
    sortBy = current_sort || 'hot'
    selectedCategory = selected_category_id ? Number(selected_category_id) : null
  })
</script>

<div class="space-y-6">
  <FlashBanner />

  <!-- Header -->
  <header class="pb-6 border-b border-default">
    <!-- Breadcrumb -->
    <nav class="flex items-center gap-1.5 text-caption text-content-tertiary mb-4">
      <a href="/boards" class="inline-flex items-center gap-1 hover:text-primary transition-colors cursor-pointer">
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
        </svg>
        게시판
      </a>
      <svg class="w-3.5 h-3.5 text-gray-400 dark:text-dark-text-disabled" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
      </svg>
      <span class="font-medium text-content">{board.name}</span>
    </nav>

    <div class="flex items-center justify-between gap-4">
      <div class="min-w-0">
        <h1 class="text-h1 text-content mb-1">{board.name}</h1>
        {#if board.description}
          <p class="text-body2 text-content-tertiary">{board.description}</p>
        {/if}
      </div>

      {#if board.allow_write !== false}
        <a
          href={`/boards/${board.slug}/posts/new`}
          class="btn btn-primary btn-md shrink-0"
        >
          <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
          </svg>
          글쓰기
        </a>
      {:else}
        <span class="text-body2 text-gray-400 dark:text-dark-text-disabled shrink-0">글쓰기 제한됨</span>
      {/if}
    </div>
  </header>
  
  <!-- Filter & Sort -->
  <div class="pb-4 border-b border-default">
    <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <!-- Categories -->
        <div class="flex items-center gap-2 overflow-x-auto">
          <button
            onclick={() => changeCategory(null)}
            class="px-3 py-1.5 rounded-xl text-body2 transition-all whitespace-nowrap {selectedCategory === null
              ? 'bg-primary text-white'
              : 'bg-surface-tertiary text-content-secondary hover:bg-gray-200 dark:hover:bg-dark-elevated'}"
          >
            전체
          </button>
          {#each categories as category (category.id)}
            <button
              onclick={() => changeCategory(category.id)}
              class="px-3 py-1.5 rounded-xl text-body2 transition-all whitespace-nowrap {selectedCategory === category.id
                ? 'text-white'
                : 'text-content-secondary bg-surface-tertiary hover:bg-gray-200 dark:hover:bg-dark-elevated'}"
              style={selectedCategory === category.id ? `background-color: ${category.color}` : ''}
            >
              {category.name}
            </button>
          {/each}
        </div>
        
        <!-- Sort -->
        <div class="flex items-center gap-1 lg:ml-4 flex-wrap">
          <button
            onclick={() => changeSort('hot')}
            class="px-3 py-1.5 rounded-xl text-body2 transition-all {sortBy === 'hot' 
              ? 'text-primary font-medium' 
              : 'text-content-tertiary hover:text-gray-900 dark:hover:text-dark-text-high'}"
          >
            <svg class="w-4 h-4 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z"/></svg> 인기
          </button>
          <button
            onclick={() => changeSort('new')}
            class="px-3 py-1.5 rounded-xl text-body2 transition-all {sortBy === 'new' 
              ? 'text-primary font-medium' 
              : 'text-content-tertiary hover:text-gray-900 dark:hover:text-dark-text-high'}"
          >
            <svg class="w-4 h-4 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg> 최신
          </button>
          <button
            onclick={() => changeSort('comments')}
            class="px-3 py-1.5 rounded-xl text-body2 transition-all {sortBy === 'comments' 
              ? 'text-primary font-medium' 
              : 'text-content-tertiary hover:text-gray-900 dark:hover:text-dark-text-high'}"
          >
            <svg class="w-4 h-4 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 8h10M7 12h6m-8 8l2.586-2.586A2 2 0 0111 17h7a2 2 0 002-2V7a2 2 0 00-2-2H6a2 2 0 00-2 2v8a2 2 0 002 2h1z"/></svg> 댓글순
          </button>

          <div class="ml-2 pl-2 border-l border-default flex items-center gap-1">
            <button
              type="button"
              onclick={() => changeListDensity('comfortable')}
              class="px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors {listDensity === 'comfortable' ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'bg-surface-tertiary text-content-secondary'}"
            >
              기본
            </button>
            <button
              type="button"
              onclick={() => changeListDensity('compact')}
              class="px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors {listDensity === 'compact' ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'bg-surface-tertiary text-content-secondary'}"
            >
              컴팩트
            </button>
          </div>
        </div>
    </div>
  </div>

  <!-- Posts List -->
  <main class="py-6">
    <div class="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <section class="lg:col-span-8 space-y-3">
        {#each posts as post (post.id)}
          <a
            href={`/boards/${board.slug}/posts/${post.id}`}
            class="block card border border-default hover:shadow-md dark:hover:shadow-black/40 hover:border-gray-300 dark:hover:border-slate-500 transition-all duration-200 {listDensity === 'compact' ? 'p-3.5' : 'p-5'}"
          >
            <div class="flex items-start gap-4">
              <!-- Content -->
              <div class="flex-1 min-w-0">
                <!-- Badges -->
                <div class="flex items-center gap-2 {listDensity === 'compact' ? 'mb-1.5' : 'mb-2'} flex-wrap">
                  {#if post.is_notice}
                    <span class="badge badge-error text-xs font-semibold"><svg class="w-3 h-3 mr-0.5 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/></svg>공지</span>
                  {/if}
                  {#if post.is_pinned}
                    <span class="badge badge-warning text-xs font-semibold"><svg class="w-3 h-3 mr-0.5 inline-block" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>고정</span>
                  {/if}
                  {#each post.categories || [] as category}
                    <CategoryBadge {category} />
                  {/each}
                </div>

                <!-- Title -->
                <h3 class="font-semibold text-content hover:text-primary transition-colors truncate {listDensity === 'compact' ? 'text-body1' : 'text-title'}">
                  {post.title}
                </h3>

                <!-- Excerpt -->
                {#if post.excerpt && listDensity !== 'compact'}
                  <p class="text-body2 text-content-secondary mt-1.5 line-clamp-2">{post.excerpt}</p>
                {/if}

                <!-- Meta -->
                <div class="flex items-center gap-4 {listDensity === 'compact' ? 'mt-2' : 'mt-3'} text-caption text-content-tertiary">
                  <span class="flex items-center gap-1.5">
                    {#if post.user?.avatar_url}
                      <img src={post.user.avatar_url} alt="" class="w-4 h-4 rounded-full object-cover" />
                    {:else}
                      <span class="w-4 h-4 bg-gray-200 dark:bg-dark-tertiary rounded-full"></span>
                    {/if}
                    <span class="font-medium">{post.user?.name || '익명'}</span>
                  </span>
                  <span>{formatRelativeTime(post.created_at)}</span>
                </div>
              </div>

              <!-- Stats -->
              <div class="hidden md:flex items-center {listDensity === 'compact' ? 'gap-4' : 'gap-6'} ml-4">
                <div class="text-right">
                  <div class="flex items-center gap-1 text-gray-600 dark:text-dark-text-low text-caption mb-0.5">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                    </svg>
                    <span class="text-xs">{listDensity === 'compact' ? '' : '조회'}</span>
                  </div>
                  <span class="text-body2 font-semibold text-content">{post.views_count || 0}</span>
                </div>
                <div class="text-right">
                  <div class="flex items-center gap-1 text-gray-600 dark:text-dark-text-low text-caption mb-0.5">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                    </svg>
                    <span class="text-xs">{listDensity === 'compact' ? '' : '댓글'}</span>
                  </div>
                  <span class="text-body2 font-semibold text-content">{post.comments_count || 0}</span>
                </div>
                <div class="text-right">
                  <div class="flex items-center gap-1 text-gray-600 dark:text-dark-text-low text-caption mb-0.5">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
                    </svg>
                    <span class="text-xs">{listDensity === 'compact' ? '' : '추천'}</span>
                  </div>
                  <span class="text-body2 font-semibold text-content">{post.likes_count || 0}</span>
                </div>
              </div>
            </div>
          </a>
          {#if adAfterPostIds.has(post.id)}
            <AdSlot variant="infeed" slot={IN_FEED_SLOT} format="fluid" layoutKey={IN_FEED_LAYOUT_KEY} />
          {/if}
        {/each}
      </section>

      <aside class="lg:col-span-4 space-y-4">
        <div class="card card-bordered p-4">
          <div class="flex items-center justify-between mb-3">
            <h3 class="text-label text-content">이 게시판 빠른 액션</h3>
            <button
              type="button"
              class="inline-flex items-center gap-1 rounded-lg border border-default px-2.5 py-1.5 text-xs font-medium text-content-secondary hover:text-warning hover:border-warning/40 transition-colors"
              onclick={toggleBoardFavorite}
            >
              <svg class="w-4 h-4 {isFavorite ? 'text-warning' : ''}" fill={isFavorite ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.914c.969 0 1.371 1.24.588 1.81l-3.975 2.888a1 1 0 00-.364 1.118l1.519 4.674c.3.921-.755 1.688-1.54 1.118l-3.975-2.888a1 1 0 00-1.176 0l-3.975 2.888c-.784.57-1.838-.197-1.539-1.118l1.519-4.674a1 1 0 00-.364-1.118L2.98 10.1c-.783-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.616-4.674z"/>
              </svg>
              {isFavorite ? '저장됨' : '즐겨찾기'}
            </button>
          </div>
          <div class="grid grid-cols-2 gap-2">
            {#if board.allow_write !== false}
              <a href={`/boards/${board.slug}/posts/new`} class="btn btn-primary btn-sm">글쓰기</a>
            {:else}
              <span class="btn btn-secondary btn-sm opacity-50 cursor-not-allowed text-center">글쓰기 제한</span>
            {/if}
            <a href="/boards?favorites=1#favorite-spaces" class="btn btn-secondary btn-sm">즐겨찾기 보기</a>
          </div>
        </div>

        <div class="card card-bordered p-4">
          <h3 class="text-label text-content mb-3">카테고리 탐색</h3>
          <div class="flex flex-wrap gap-2">
            <button
              type="button"
              onclick={() => changeCategory(null)}
              class="px-2.5 py-1 rounded-lg text-xs font-medium transition-colors {selectedCategory === null ? 'bg-primary text-white' : 'bg-surface-tertiary text-content-secondary'}"
            >
              전체
            </button>
            {#each categories.slice(0, 8) as category (category.id)}
              <button
                type="button"
                onclick={() => changeCategory(category.id)}
                class="px-2.5 py-1 rounded-lg text-xs font-medium transition-colors {selectedCategory === category.id ? 'text-white' : 'text-gray-700 dark:text-dark-text-medium bg-surface-tertiary'}"
                style={selectedCategory === category.id ? `background-color: ${category.color}` : ''}
              >
                {category.name}
              </button>
            {/each}
          </div>
        </div>

        <div class="card card-bordered p-4">
          <h3 class="text-label text-content mb-3">지금 인기 글</h3>
          <div class="space-y-2.5">
            {#if popular_posts.length > 0}
              {#each popular_posts as item, index}
                <a href={`/boards/${board.slug}/posts/${item.id}`} class="flex items-start gap-2.5 rounded-lg p-2 hover:bg-gray-50 dark:hover:bg-dark-tertiary transition-colors">
                  <span class="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold {index < 3 ? 'bg-primary text-white' : 'bg-surface-tertiary text-content-tertiary'}">
                    {index + 1}
                  </span>
                  <div class="min-w-0">
                    <p class="text-sm font-medium text-gray-800 dark:text-dark-text-high line-clamp-1">{item.title}</p>
                    <p class="text-xs text-content-tertiary mt-0.5">조회 {item.views_count || 0} · 댓글 {item.comments_count || 0}</p>
                  </div>
                </a>
              {/each}
            {:else}
              <p class="text-sm text-content-tertiary">아직 인기 글 데이터가 없어요.</p>
            {/if}
          </div>
        </div>
      </aside>
    </div>
    
    {#if posts.length === 0}
      <div class="text-center py-20">
        <div class="w-20 h-20 bg-surface-tertiary rounded-full flex items-center justify-center mx-auto mb-4">
          <svg class="w-10 h-10 text-gray-400 dark:text-dark-text-disabled" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
          </svg>
        </div>
        <h3 class="text-h2 text-content mb-2">아직 게시글이 없습니다</h3>
        <p class="text-body2 text-content-tertiary mb-6">
          {board.allow_write !== false ? '첫 번째 글을 작성해보세요!' : '현재 글쓰기가 제한되어 있습니다.'}
        </p>
        {#if board.allow_write !== false}
          <a
            href={`/boards/${board.slug}/posts/new`}
            class="btn btn-primary btn-md"
          >
            글쓰기
          </a>
        {/if}
      </div>
    {/if}
    
    <!-- Pagination -->
    {#if pagination.total_pages > 1}
      <div class="flex items-center justify-center gap-2 mt-8">
        <button
          onclick={() => goToPage(pagination.current_page - 1)}
          disabled={pagination.current_page === 1}
          class="btn btn-secondary btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
          </svg>
          이전
        </button>
        
        <span class="px-4 py-2 text-body2 text-content-tertiary">
          {pagination.current_page} / {pagination.total_pages}
        </span>
        
        <button
          onclick={() => goToPage(pagination.current_page + 1)}
          disabled={pagination.current_page === pagination.total_pages}
          class="btn btn-secondary btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          다음
          <svg class="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
        </button>
      </div>
    {/if}
  </main>
</div>
